import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import type { SubscriptionStatus } from '@/modules/subscriptions/types';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';
import {
  useCancelAdminTrial,
  useExtendAdminTrial,
  useResumeAdminTrial,
  useSuspendAdminTrial,
} from '../hooks/useAdminTrialMutations';
import type { AdminTrialItem } from '../types/adminTrial';
import type { TrialCustomerIdentity } from '../types/trialCustomerIdentity';
import { hasActionableCustomerIdentity } from '../types/trialCustomerIdentity';
import {
  calculateExtendedTrialEnd,
  formatDateTime,
  formatShortId,
  formatTrialApiError,
  getTrialActionAvailability,
  safeDisplay,
} from '../utils/trialHelpers';
import { TrialAdminActions } from './TrialAdminActions';
import { TrialCustomerIdentitySection } from './TrialCustomerIdentitySection';
import { TrialDaysBadge } from './TrialDaysBadge';
import { EntitlementStatusBadge } from './EntitlementStatusBadge';
import { TrialProductTimelineSection } from './TrialProductTimelineSection';
import { TrialTechnicalIdsSection } from './TrialTechnicalIdsSection';

interface TrialDetailDrawerProps {
  trial: AdminTrialItem;
  identity: TrialCustomerIdentity;
  canSuspend: boolean;
  canCancel: boolean;
  canUpdate: boolean;
  onClose: () => void;
  onTrialUpdated: (trial: AdminTrialItem) => void;
}

type DialogType = 'extend' | 'suspend' | 'cancel' | 'resume' | null;

const EXTEND_DAYS_MIN = 1;
const EXTEND_DAYS_MAX = 365;

function ActionTargetSummary({ identity }: { identity: TrialCustomerIdentity }) {
  const { t } = useTranslation();
  const customerName = safeDisplay(identity.customerName);
  const tenantName = safeDisplay(identity.tenantName);
  const shortId = formatShortId(identity.subscriptionId);

  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm space-y-1.5 min-w-0">
      <p className="font-medium text-foreground">{t('trials.dialogs.affectsCustomer')}</p>
      <p className="font-semibold text-foreground break-words">
        {customerName ?? t('trials.identity.unavailable')}
      </p>
      <p className="text-foreground break-words">
        {tenantName ?? t('trials.identity.unavailable')}
      </p>
      {identity.customerEmail ? (
        <p className="text-muted-foreground break-all text-xs">{identity.customerEmail}</p>
      ) : null}
      {shortId ? (
        <p className="font-mono text-xs text-muted-foreground" title={identity.subscriptionId}>
          {shortId}
        </p>
      ) : null}
    </div>
  );
}

function DialogActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function TrialDetailDrawer({
  trial,
  identity,
  canSuspend,
  canCancel,
  canUpdate,
  onClose,
  onTrialUpdated,
}: TrialDetailDrawerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const panelRef = useRef<HTMLDivElement>(null);

  const [dialog, setDialog] = useState<DialogType>(null);
  const [reason, setReason] = useState('');
  const [extendDays, setExtendDays] = useState('7');
  const [cancelImmediate, setCancelImmediate] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const extendMutation = useExtendAdminTrial();
  const suspendMutation = useSuspendAdminTrial();
  const resumeMutation = useResumeAdminTrial();
  const cancelMutation = useCancelAdminTrial();

  const identityConfirmed = hasActionableCustomerIdentity(identity);

  const extendDaysNum = Number.parseInt(extendDays, 10);
  const extendDaysValid =
    Number.isFinite(extendDaysNum) &&
    extendDaysNum >= EXTEND_DAYS_MIN &&
    extendDaysNum <= EXTEND_DAYS_MAX;
  const projectedEnd = extendDaysValid ? calculateExtendedTrialEnd(trial, extendDaysNum) : null;

  const isPending =
    extendMutation.isPending ||
    suspendMutation.isPending ||
    resumeMutation.isPending ||
    cancelMutation.isPending;

  const headerTitle = useMemo(() => {
    return (
      safeDisplay(identity.customerName) ??
      safeDisplay(identity.tenantName) ??
      t('trials.detail.title')
    );
  }, [identity.customerName, identity.tenantName, t]);

  const headerSubtitle = useMemo(() => {
    const parts = [
      safeDisplay(identity.tenantName),
      identity.productName && identity.planName
        ? `${identity.productName} · ${identity.planName}`
        : (identity.productName ?? identity.planName),
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [identity]);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !dialog) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, onClose]);

  const dismissActionDialog = () => {
    setDialog(null);
    setReason('');
    setExtendDays('7');
    setCancelImmediate(true);
  };

  const handleMutationError = (err: unknown) => {
    setActionError(formatTrialApiError(err, t));
  };

  const openDialog = (type: DialogType) => {
    setActionError(null);
    setActionSuccess(null);
    setDialog(type);
  };

  const handleExtend = () => {
    if (!identityConfirmed || !extendDaysValid) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setActionError(t('trials.validation.reasonRequired'));
      return;
    }
    setActionError(null);
    extendMutation.mutate(
      {
        subscriptionId: trial.subscription_id,
        data: { days: extendDaysNum, reason: trimmedReason, version: trial.version },
      },
      {
        onSuccess: (updated) => {
          dismissActionDialog();
          setActionSuccess(t('trials.actions.extendSuccess'));
          onTrialUpdated(updated);
        },
        onError: handleMutationError,
      },
    );
  };

  const handleSuspend = () => {
    if (!identityConfirmed) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setActionError(t('trials.validation.reasonRequired'));
      return;
    }
    setActionError(null);
    suspendMutation.mutate(
      {
        subscriptionId: trial.subscription_id,
        data: { reason: trimmedReason, version: trial.version },
      },
      {
        onSuccess: (updated) => {
          dismissActionDialog();
          setActionSuccess(t('trials.actions.suspendSuccess'));
          onTrialUpdated(updated);
        },
        onError: handleMutationError,
      },
    );
  };

  const handleResume = () => {
    if (!identityConfirmed) return;
    if (getTrialActionAvailability(trial).resumeRequiresExtendFirst) {
      setActionError(t('trials.errors.trialExpiredResume'));
      return;
    }
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setActionError(t('trials.validation.reasonRequired'));
      return;
    }
    setActionError(null);
    resumeMutation.mutate(
      {
        subscriptionId: trial.subscription_id,
        data: { reason: trimmedReason, version: trial.version },
      },
      {
        onSuccess: (updated) => {
          dismissActionDialog();
          setActionSuccess(t('trials.actions.resumeSuccess'));
          onTrialUpdated(updated);
        },
        onError: handleMutationError,
      },
    );
  };

  const handleCancel = () => {
    if (!identityConfirmed) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setActionError(t('trials.validation.reasonRequired'));
      return;
    }
    setActionError(null);
    cancelMutation.mutate(
      {
        subscriptionId: trial.subscription_id,
        data: {
          reason: trimmedReason,
          effective: cancelImmediate ? 'immediate' : 'period_end',
          version: trial.version,
        },
      },
      {
        onSuccess: (updated) => {
          dismissActionDialog();
          setActionSuccess(t('trials.actions.cancelSuccess'));
          onTrialUpdated(updated);
        },
        onError: handleMutationError,
      },
    );
  };

  const metaRows: { label: string; value: string }[] = [
    { label: t('trials.detail.version'), value: String(trial.version) },
    { label: t('trials.detail.created'), value: formatDateTime(trial.created_at, locale) },
    { label: t('trials.detail.updated'), value: formatDateTime(trial.updated_at, locale) },
  ];

  const status = trial.subscription_status as SubscriptionStatus;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={() => !dialog && onClose()}
        aria-hidden="true"
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="trial-detail-title"
        tabIndex={-1}
        className={cn(
          'fixed inset-y-0 end-0 z-50 flex w-full max-w-[min(100vw,28rem)] sm:max-w-lg flex-col border-s border-border bg-card shadow-xl',
          'animate-in slide-in-from-end duration-200',
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border p-4 sm:p-5 shrink-0">
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground">{t('trials.detail.title')}</p>
            <h2
              id="trial-detail-title"
              className="text-lg font-semibold text-foreground break-words"
            >
              {headerTitle}
            </h2>
            {headerSubtitle ? (
              <p className="text-sm text-muted-foreground break-words">{headerSubtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('trials.detail.close')}
          >
            <Icons.X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-5 space-y-5 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <SubscriptionStatusBadge status={status} />
            <EntitlementStatusBadge status={trial.entitlement_status} />
            <TrialDaysBadge trial={trial} />
          </div>

          {actionSuccess ? (
            <p
              role="status"
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-400"
            >
              {actionSuccess}
            </p>
          ) : null}
          {actionError ? (
            <p
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {actionError}
            </p>
          ) : null}

          <TrialCustomerIdentitySection identity={identity} />
          <TrialProductTimelineSection identity={identity} locale={locale} />
          <TrialTechnicalIdsSection identity={identity} />

          <div className="space-y-2 pt-1">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('trials.detail.subscriptionMeta')}
            </h3>
            <dl className="space-y-2 rounded-lg border border-border/60 bg-muted/10 p-3">
              {metaRows.map((row) => (
                <div key={row.label} className="flex justify-between gap-3 text-xs min-w-0">
                  <dt className="font-medium text-muted-foreground shrink-0">{row.label}</dt>
                  <dd className="text-foreground text-end tabular-nums break-words min-w-0">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="border-t border-border p-4 sm:p-5 space-y-3 shrink-0 bg-card">
          <p className="text-sm font-semibold text-foreground">{t('trials.actions.title')}</p>
          <TrialAdminActions
            trial={trial}
            identityConfirmed={identityConfirmed}
            canSuspend={canSuspend}
            canCancel={canCancel}
            canUpdate={canUpdate}
            isPending={isPending}
            onExtend={() => openDialog('extend')}
            onSuspend={() => openDialog('suspend')}
            onResume={() => openDialog('resume')}
            onCancel={() => openDialog('cancel')}
          />
        </div>
      </aside>

      {dialog === 'extend' ? (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="extend-trial-title"
            className="w-full max-w-md space-y-4 rounded-xl border border-border bg-card p-5 sm:p-6 shadow-xl max-h-[min(90vh,640px)] overflow-y-auto"
          >
            <h3 id="extend-trial-title" className="text-lg font-semibold text-foreground">
              {t('trials.dialogs.extend.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('trials.dialogs.extend.description')}
            </p>
            <ActionTargetSummary identity={identity} />
            <dl className="rounded-lg border border-border bg-muted/30 p-3 text-sm space-y-2">
              <div className="flex justify-between gap-2 flex-wrap">
                <dt className="text-muted-foreground">{t('trials.dialogs.extend.currentEnd')}</dt>
                <dd className="text-foreground font-medium text-end">
                  {formatDateTime(trial.trial_ends_at, locale)}
                </dd>
              </div>
              {projectedEnd ? (
                <div className="flex justify-between gap-2 flex-wrap">
                  <dt className="text-muted-foreground">{t('trials.dialogs.extend.newEnd')}</dt>
                  <dd className="text-foreground font-medium text-end">
                    {formatDateTime(projectedEnd.toISOString(), locale)}
                  </dd>
                </div>
              ) : null}
            </dl>
            <div className="space-y-1.5">
              <label htmlFor="extend-days" className="text-sm text-foreground">
                {t('trials.dialogs.extend.daysLabel')}
              </label>
              <input
                id="extend-days"
                type="number"
                min={EXTEND_DAYS_MIN}
                max={EXTEND_DAYS_MAX}
                className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
              />
              {!extendDaysValid && extendDays.length > 0 ? (
                <p className="text-xs text-destructive">
                  {t('trials.validation.extendDays', {
                    min: EXTEND_DAYS_MIN,
                    max: EXTEND_DAYS_MAX,
                  })}
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <label htmlFor="extend-reason" className="text-sm text-foreground">
                {t('trials.dialogs.reasonRequired')}
              </label>
              <input
                id="extend-reason"
                type="text"
                className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <DialogActions>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={dismissActionDialog}
                disabled={isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                className="w-full sm:w-auto"
                isLoading={extendMutation.isPending}
                disabled={!extendDaysValid}
                onClick={handleExtend}
              >
                {t('trials.dialogs.extend.confirm')}
              </Button>
            </DialogActions>
          </div>
        </div>
      ) : null}

      {dialog === 'suspend' ? (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="suspend-trial-title"
            className="w-full max-w-md space-y-4 rounded-xl border border-border bg-card p-5 sm:p-6 shadow-xl max-h-[min(90vh,640px)] overflow-y-auto"
          >
            <h3 id="suspend-trial-title" className="text-lg font-semibold text-foreground">
              {t('trials.dialogs.suspend.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('trials.dialogs.suspend.description')}
            </p>
            <ActionTargetSummary identity={identity} />
            <div className="space-y-1.5">
              <label htmlFor="suspend-reason" className="text-sm text-foreground">
                {t('trials.dialogs.reasonRequired')}
              </label>
              <input
                id="suspend-reason"
                type="text"
                className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <DialogActions>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={dismissActionDialog}
                disabled={isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                className="w-full sm:w-auto"
                isLoading={suspendMutation.isPending}
                onClick={handleSuspend}
              >
                {t('trials.dialogs.suspend.confirm')}
              </Button>
            </DialogActions>
          </div>
        </div>
      ) : null}

      {dialog === 'resume' ? (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="resume-trial-title"
            className="w-full max-w-md space-y-4 rounded-xl border border-border bg-card p-5 sm:p-6 shadow-xl max-h-[min(90vh,640px)] overflow-y-auto"
          >
            <h3 id="resume-trial-title" className="text-lg font-semibold text-foreground">
              {t('trials.dialogs.resume.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('trials.dialogs.resume.description')}
            </p>
            <ActionTargetSummary identity={identity} />
            <div className="space-y-1.5">
              <label htmlFor="resume-reason" className="text-sm text-foreground">
                {t('trials.dialogs.reasonRequired')}
              </label>
              <input
                id="resume-reason"
                type="text"
                className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <DialogActions>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={dismissActionDialog}
                disabled={isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                className="w-full sm:w-auto"
                isLoading={resumeMutation.isPending}
                onClick={handleResume}
              >
                {t('trials.dialogs.resume.confirm')}
              </Button>
            </DialogActions>
          </div>
        </div>
      ) : null}

      {dialog === 'cancel' ? (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-trial-title"
            className="w-full max-w-md space-y-4 rounded-xl border border-destructive/30 bg-card p-5 sm:p-6 shadow-xl max-h-[min(90vh,640px)] overflow-y-auto"
          >
            <h3 id="cancel-trial-title" className="text-lg font-semibold text-foreground">
              {t('trials.dialogs.cancel.title')}
            </h3>
            <p className="text-sm text-destructive font-medium">
              {t('trials.dialogs.cancel.warning')}
            </p>
            <ActionTargetSummary identity={identity} />
            <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={cancelImmediate}
                onChange={(e) => setCancelImmediate(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border shrink-0"
              />
              <span>{t('subscriptions.cancelDialog.immediate')}</span>
            </label>
            <div className="space-y-1.5">
              <label htmlFor="cancel-reason" className="text-sm text-foreground">
                {t('trials.dialogs.reasonRequired')}
              </label>
              <input
                id="cancel-reason"
                type="text"
                className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <DialogActions>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={dismissActionDialog}
                disabled={isPending}
              >
                {t('common.back')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="w-full sm:w-auto"
                isLoading={cancelMutation.isPending}
                onClick={handleCancel}
              >
                {t('trials.dialogs.cancel.confirm')}
              </Button>
            </DialogActions>
          </div>
        </div>
      ) : null}
    </>
  );
}
